import { create } from "zustand";
import type { ButtonProps } from "@/components/ui/button";
import * as React from "react";

export type AlertDialogAction = {
  label: string;
  onClick: () => void;
  variant?: ButtonProps["variant"];
  value: unknown;
};

type AlertDialogMode = "alert" | "confirm" | "prompt";

type AlertDialogState = {
  isOpen: boolean;
  title?: React.ReactNode;
  description?: React.ReactNode;
  actions: AlertDialogAction[];
  resolvePromise?: (value: unknown | PromiseLike<unknown>) => void;
  mode?: AlertDialogMode;
  promptPlaceholder?: string;
  promptDefaultValue?: string;
};

type AlertDialogStore = AlertDialogState & {
  show: (
    title: React.ReactNode,
    description: React.ReactNode,
    actions: AlertDialogAction[],
    resolve: (value: unknown | PromiseLike<unknown>) => void,
  ) => void;
  hide: () => void;
  reset: () => void;
  showPrompt: (
    title: React.ReactNode,
    description: React.ReactNode,
    placeholder: string | undefined,
    defaultValue: string | undefined,
    resolve: (value: unknown | PromiseLike<unknown>) => void,
  ) => void;
};

const initialState: AlertDialogState = {
  isOpen: false,
  title: undefined,
  description: undefined,
  actions: [],
  resolvePromise: undefined,
};

export const useAlertDialogStore = create<AlertDialogStore>((set) => ({
  ...initialState,
  show: (title, description, actions, resolve) =>
    set({
      isOpen: true,
      title,
      description,
      actions,
      resolvePromise: resolve,
      mode: "alert",
      promptPlaceholder: undefined,
      promptDefaultValue: undefined,
    }),
  hide: () => set({ isOpen: false }),
  reset: () => set({ ...initialState }),
  showPrompt: (title, description, placeholder, defaultValue, resolve) =>
    set({
      isOpen: true,
      title,
      description,
      actions: [
        { label: "Okay", value: true, variant: "default", onClick: () => {} },
        {
          label: "Cancel",
          value: false,
          variant: "outline",
          onClick: () => {},
        },
      ],
      resolvePromise: resolve,
      mode: "prompt",
      promptPlaceholder: placeholder,
      promptDefaultValue: defaultValue,
    }),
}));

/**
 * Shows an alert dialog and returns a promise that resolves with the value of the action taken by the user.
 * @param title The title of the dialog.
 * @param description The description/content of the dialog.
 * @param actions An array of action objects. Each object should have `label`, `value`, and optionally `variant` and `onClick` (though onClick in actions will be overridden to resolve the promise).
 * @returns A promise that resolves with the `value` of the chosen action, or `null` if dismissed.
 */
export function showAlert<T = unknown>({
  title,
  description,
  actions,
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  actions: Array<Omit<AlertDialogAction, "onClick" | "value"> & { value: T }>;
}): Promise<T | null> {
  return new Promise((resolve) => {
    const mappedActions: AlertDialogAction[] = actions.map((action) => ({
      ...action,
      label: action.label,
      variant: action.variant,
      value: action.value,
      onClick: () => {
        // User-defined onClick is not directly used here as we need to resolve the promise
        // If a custom onClick is needed, it should be handled by the component calling showAlert
        // based on the resolved promise value.
      },
    }));
    useAlertDialogStore
      .getState()
      .show(
        title,
        description,
        mappedActions,
        resolve as (value: unknown) => void,
      );
  });
}

/**
 * Shows a confirmation dialog with "Okay" and "Cancel" buttons.
 * @param title The title of the confirmation dialog.
 * @param description The description/content of the confirmation dialog.
 * @returns A promise that resolves with `true` if "Okay" is clicked, and `false` otherwise (Cancel or dismissal).
 */
export function confirm({
  title,
  description,
  destructive = false,
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  destructive?: boolean;
}): Promise<boolean> {
  return new Promise((resolve) => {
    const actions: AlertDialogAction[] = [
      {
        label: "Okay",
        value: true,
        variant: destructive ? "destructiveGhost" : "default",
        onClick: () => {
          /* Handled by GlobalAlertDialog component */
        },
      },
      {
        label: "Cancel",
        value: false,
        variant: "outline",
        onClick: () => {
          /* Handled by GlobalAlertDialog component */
        },
      },
    ];
    useAlertDialogStore
      .getState()
      .show(title, description, actions, resolve as (value: unknown) => void);
  });
}

export function promptText({
  title,
  description,
  placeholder,
  defaultValue,
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  placeholder?: string;
  defaultValue?: string;
}): Promise<string | null> {
  return new Promise((resolve) => {
    useAlertDialogStore
      .getState()
      .showPrompt(
        title,
        description ?? null,
        placeholder,
        defaultValue,
        resolve as (value: unknown) => void,
      );
  });
}
